import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Group,
  Modal,
  Notification,
  Space,
  Stack,
  Loader,
  Text,
  TextInput,
  InputWrapper,
  Paper,
  Badge,
  Divider,
} from "@mantine/core";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useForm } from "@mantine/form";
import { IconCreditCard } from "@tabler/icons-react";

import { db } from "../../firebase";
import { Check } from "tabler-icons-react";
import { getDoc, doc } from "firebase/firestore";

import {
  createPaypalOrder,
  capturePaypalPayment,
} from "./hooks/paypal-functions";
import CreditConsumptionChart, { Transaction } from "./credit-consumption-chart"; // Import the chart component

const paypalClientID =
  "AZgwK9jtwNgFQAl8UiFveSRPxjjJ_d_4tbYahVZcTCuQcnzJwlr4Y6Cb93Q-5Su6585qhg72AOuvuxq5";

export interface PayProps {
  companyId: string;
}

export interface CreditDoc {
  isEnterprisePriced?: boolean;
  creditBalance?: number;
  processedTransactions?: Transaction[];
}

const Pay = ({ companyId }: PayProps) => {
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState("");
  const [color, setColor] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchedCredits, setFetchedCredits] = useState(false);
  const [creditDoc, setCreditDoc] = useState<CreditDoc | undefined>();
  const [creditStatus, setcreditStatus] = useState("select");
  const [opened, setOpened] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<"10k" | "20k" | "100k" | "other">("20k");

  const form = useForm({
    initialValues: {
      creditOrder: 20000,
      termsOfService: false,
    },

    validate: {
      creditOrder: (value: number) => (value >= 1000 ? null : "Enter at least 1000"),
    },
  });

  if (!fetchedCredits) {
    const fetchCredits = async () => {
      setFetchedCredits(true);
      setLoading(true);

      const docRef = doc(db, "credits", companyId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setCreditDoc(docSnap.data() as CreditDoc);
      } else {
        console.error("No credit document");
      }
      setLoading(false);
    };
    fetchCredits();
  }

  // Hide the entire page if `creditDoc.isEnterprisePriced` is true
  if (creditDoc?.isEnterprisePriced) {
    return null; // Render nothing
  }

  return (
    <>
      <Paper p="lg" radius="md" withBorder style={{ border: "1px solid #e9ecef" }}>
      <Group position="apart" mb="md" align="flex-start">
        <div>
          <Text size="md" weight={600} color="dark">
            Credit Balance & Usage
          </Text>
          <Text size="xs" color="dimmed" mt={2}>
            Credits are consumed when compiling data reports, generating exports, and running automated notifications.
          </Text>
        </div>
        <Button
          size="sm"
          color="cap-navy"
          leftIcon={<IconCreditCard size={16} />}
          onClick={() => setOpened(true)}
        >
          Buy Credits
        </Button>
      </Group>

      <Divider my="sm" />

      {loading ? (
        <Loader style={{ margin: "2em auto", display: "block" }} />
      ) : (
        <>
          <Group spacing="xl" my="md">
            <Paper p="md" radius="sm" style={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef", minWidth: 220 }}>
              <Text size="xs" weight={700} color="dimmed" style={{ letterSpacing: "0.05em" }}>
                AVAILABLE CREDITS
              </Text>
              <Group spacing="xs" align="baseline" mt={4}>
                <Text
                  size="xl"
                  weight={700}
                  style={{
                    fontSize: 28,
                    color: (creditDoc?.creditBalance ?? 0) <= 0 ? "#e03131" : "#1A1B1E",
                  }}
                >
                  {(creditDoc?.creditBalance ?? 0).toLocaleString()}
                </Text>
                <Text size="xs" color="dimmed">
                  credits
                </Text>
              </Group>
              <Badge
                size="xs"
                color={(creditDoc?.creditBalance ?? 0) <= 0 ? "red" : "green"}
                variant="light"
                mt={4}
              >
                {(creditDoc?.creditBalance ?? 0) <= 0 ? "Credits Depleted" : "Account Active"}
              </Badge>
            </Paper>
          </Group>

          {creditDoc?.processedTransactions && (
            <div style={{ marginTop: "1.5rem" }}>
              <Text size="sm" weight={600} color="dark" mb="xs">
                Weekly Consumption Activity
              </Text>
              <CreditConsumptionChart processedTransactions={creditDoc.processedTransactions} />
            </div>
          )}
        </>
      )}
    </Paper>
      <Modal
        opened={opened}
        onClose={() => {
          setOpened(false);
          setcreditStatus("select");
          setSelectedPreset("20k");
          form.reset();
        }}
        title="Credit purchase"
      >
        <Box px="xs" pb="xs">
          {creditStatus === "select" && (
            <Box sx={{ maxWidth: 400 }}>
              <form onSubmit={form.onSubmit(() => setcreditStatus("pay"))}>
                <InputWrapper
                  label="Select credit amount"
                  styles={{
                    label: {
                      fontSize: "16px",
                    },
                  }}
                >
                  <Group mb="md" spacing="xs" grow>
                    <Button
                      variant={selectedPreset === "10k" ? "filled" : "outline"}
                      color="cap-navy"
                      onClick={() => {
                        setSelectedPreset("10k");
                        form.setFieldValue("creditOrder", 10000);
                      }}
                      size="sm"
                    >
                      10k
                    </Button>
                    <Button
                      variant={selectedPreset === "20k" ? "filled" : "outline"}
                      color="cap-navy"
                      onClick={() => {
                        setSelectedPreset("20k");
                        form.setFieldValue("creditOrder", 20000);
                      }}
                      size="sm"
                    >
                      20k
                    </Button>
                    <Button
                      variant={selectedPreset === "100k" ? "filled" : "outline"}
                      color="cap-navy"
                      onClick={() => {
                        setSelectedPreset("100k");
                        form.setFieldValue("creditOrder", 100000);
                      }}
                      size="sm"
                    >
                      100k
                    </Button>
                    <Button
                      variant={selectedPreset === "other" ? "filled" : "outline"}
                      color="cap-navy"
                      onClick={() => {
                        setSelectedPreset("other");
                      }}
                      size="sm"
                    >
                      Other
                    </Button>
                  </Group>
                </InputWrapper>

                {selectedPreset === "other" && (
                  <TextInput
                    required
                    size="md"
                    label="Purchase credits"
                    {...form.getInputProps("creditOrder")}
                  />
                )}

                <Checkbox
                  mt="md"
                  label={
                    <Text size="sm" component="span" style={{ fontFamily: "inherit" }}>
                      I agree to the{" "}
                      <a
                        href="https://captivadata.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#228be6", textDecoration: "underline", fontFamily: "inherit" }}
                      >
                        terms and conditions
                      </a>
                      .
                    </Text>
                  }
                  {...form.getInputProps("termsOfService", {
                    type: "checkbox",
                  })}
                />

                <Group position="right" mt="md">
                  <Button
                    size="md"
                    color="cap-navy"
                    type="submit"
                    disabled={!form.values.termsOfService}
                  >
                    Pay $
                    {(
                      Math.ceil(form.values.creditOrder * 0.001 * 100) / 100
                    ).toFixed(2)}
                    (~R
                    {(
                      Math.ceil(form.values.creditOrder * 0.02 * 100) / 100
                    ).toFixed(2)}
                    )
                  </Button>
                </Group>
              </form>
            </Box>
          )}
          {creditStatus === "pay" && (
            <Box sx={{ maxWidth: 400 }}>
              <PayPalScriptProvider
                options={{
                  clientId: paypalClientID,
                  "client-id": paypalClientID,
                  currency: "USD",
                  intent: "capture",
                }}
              >
                <PayPalButtons
                  style={{
                    color: "blue",
                    shape: "pill",
                  }}
                  createOrder={async () =>
                    (
                      await createPaypalOrder(
                        form.values.creditOrder,
                        companyId
                      )
                    ).id
                  }
                  onApprove={async (data: { orderID: string }) => {
                    await capturePaypalPayment(data.orderID, companyId)
                      .then(() => {
                        setOpened(false);
                        setColor("teal");
                        setTitle("Purchase successful!");
                        setMessage(
                          `${form.values.creditOrder} credits have been added to your account.`
                        );
                        setcreditStatus("complete");
                      })
                      .catch(() => {
                        setcreditStatus("select");
                        setOpened(false);
                        setColor("red");
                        setTitle("Transaction error");
                        setMessage(
                          `Could not process the transaction. Please try again later.`
                        );
                      })
                      .finally(() => {
                        setFetchedCredits(false);
                      });
                  }}
                  onError={(error) => {
                    console.log("ERROR!!", error);
                  }}
                />
              </PayPalScriptProvider>
            </Box>
          )}
        </Box>
      </Modal>
      {message && (
        <Notification
          icon={<Check size={18} />}
          color={color}
          title={title}
          style={{ position: "absolute", bottom: "20px", right: "20px" }}
          onClose={() => setMessage("")}
        >
          {message}
        </Notification>
      )}
    </>
  );
};

export default Pay;

